import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Clock, Calendar, Loader2 } from 'lucide-react';
import { leaveApi } from '../../services/api';

interface PendingRequest {
  applicationId: string;
  employeeName: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  submittedAt: string;
}

export default function SupervisorDashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingRecommendations, setPendingRecommendations] = useState<PendingRequest[]>([]);

  useEffect(() => {
    const fetchPendingRecommendations = async () => {
      setIsLoading(true);
      setError('');

      try {
        const response = await leaveApi.listApplications({
          pendingFor: 'supervisor',
        }) as { applications: PendingRequest[] };
        setPendingRecommendations(response.applications || []);
      } catch (err: any) {
        console.error('Error fetching pending recommendations:', err);
        setError(err.message || 'Failed to load pending recommendations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPendingRecommendations();
  }, []);

  const formatLeaveType = (type: string) => {
    return type.charAt(0) + type.slice(1).toLowerCase();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Supervisor Dashboard</h1>
        <p className="text-sm text-gray-600">Manage your team's leave requests</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Pending Requests</p>
              <p className="mt-1 text-3xl font-bold text-yellow-600">
                {isLoading ? '-' : pendingRecommendations.length}
              </p>
            </div>
            <div className="rounded-full bg-yellow-100 p-3">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <Link to="/calendar" className="card p-6 transition-colors hover:bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Team Calendar</p>
              <p className="mt-1 text-sm text-gray-600">View team schedule</p>
            </div>
            <div className="rounded-full bg-blue-100 p-3">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Link>

        <Link to="/reports/team" className="card p-6 transition-colors hover:bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Team Reports</p>
              <p className="mt-1 text-sm text-gray-600">View statistics</p>
            </div>
            <div className="rounded-full bg-purple-100 p-3">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Pending Recommendations */}
      <div className="card">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Pending Your Recommendation</h2>
          {!isLoading && pendingRecommendations.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
              {pendingRecommendations.length} pending
            </span>
          )}
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : pendingRecommendations.length === 0 ? (
            <p className="text-center text-gray-500">No pending recommendations</p>
          ) : (
            <div className="space-y-4">
              {pendingRecommendations.map(request => (
                <div
                  key={request.applicationId}
                  className="flex items-center justify-between rounded-lg border border-yellow-200 bg-yellow-50 p-4"
                >
                  <div>
                    <p className="font-medium text-gray-900">{request.employeeName}</p>
                    <p className="text-sm text-gray-600">
                      {formatLeaveType(request.leaveType)} Leave: {request.startDate} - {request.endDate} ({request.totalDays} {request.totalDays === 1 ? 'day' : 'days'})
                    </p>
                    <p className="text-xs text-gray-500">
                      Submitted: {request.submittedAt ? new Date(request.submittedAt).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to={`/leave/${request.applicationId}`}
                      className="btn-primary text-sm"
                    >
                      Review
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
